import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { InfoCircle, Warning2 } from "iconsax-react";
import DBButton from "../../component/global/DBButton";
import DBModal from "../../component/global/DBModal";
import CoinPurchaseModal from "../incentives/CoinPurchaseModal";
import { formatNumber } from "../incentives/helpers";
import { aiBillingApi, isInsufficientCoins } from "./api";
import "./style.scss";

export { isInsufficientCoins };

function CoinGlyph() {
    return (
        <span className="ai-coin-chip__glyph" aria-hidden="true">
            <span />
        </span>
    );
}

export function AiCoinHint({
    operation,
    quantity = 1,
    className = "",
    mode = "badge",
}) {
    const { t, i18n } = useTranslation();
    const [coins, setCoins] = useState(null);
    const locale = i18n.language === "fa" ? "fa-IR" : "en-US";

    useEffect(() => {
        if (!operation) return undefined;
        let cancelled = false;
        aiBillingApi
            .quote({ operation, quantity })
            .then((quote) => {
                if (!cancelled) setCoins(quote.coins_required);
            })
            .catch(() => {
                if (!cancelled) setCoins(null);
            });
        return () => {
            cancelled = true;
        };
    }, [operation, quantity]);

    if (coins == null) return null;

    const amount = formatNumber(coins, locale);
    const tooltip = t("ai.coins.hint.tooltip", { amount });
    const overlay = mode === "overlay" || mode === "hover";

    return (
        <span
            className={`ai-coin-chip${
                overlay ? " ai-coin-chip--overlay" : ""
            } ${className}`.trim()}
            title={tooltip}
            data-tooltip={tooltip}
            aria-label={tooltip}
        >
            <CoinGlyph />
            <span className="ai-coin-chip__amount">{amount}</span>
        </span>
    );
}

export function useAiCoinGate() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [purchaseOpen, setPurchaseOpen] = useState(false);
    const [justPurchased, setJustPurchased] = useState(false);
    const runRef = useRef(null);
    const cancelRef = useRef(null);
    const specRef = useRef(null);
    const confirmedRef = useRef(false);
    const requestIdRef = useRef(0);

    const resetSession = useCallback(() => {
        runRef.current = null;
        cancelRef.current = null;
        specRef.current = null;
        confirmedRef.current = false;
        setJustPurchased(false);
    }, []);

    const close = useCallback(() => {
        const onCancel = cancelRef.current;
        requestIdRef.current += 1;
        setOpen(false);
        setPurchaseOpen(false);
        setError("");
        resetSession();
        onCancel?.();
    }, [resetSession]);

    const openPurchaseIfNeeded = useCallback((data) => {
        if (data && data.sufficient === false && data.can_purchase !== false) {
            setPurchaseOpen(true);
        }
    }, []);

    const loadQuote = useCallback(
        (spec, fallback) => {
            const requestId = requestIdRef.current;
            setLoading(true);
            setError("");
            return aiBillingApi
                .quote(spec)
                .then((data) => {
                    if (requestId !== requestIdRef.current) return null;
                    setQuote(data);
                    openPurchaseIfNeeded(data);
                    return data;
                })
                .catch(() => {
                    if (requestId !== requestIdRef.current) return null;
                    if (fallback) {
                        setQuote(fallback);
                        openPurchaseIfNeeded(fallback);
                        return fallback;
                    }
                    setError("ai.coins.quote.error");
                    return null;
                })
                .finally(() => {
                    if (requestId === requestIdRef.current) setLoading(false);
                });
        },
        [openPurchaseIfNeeded]
    );

    const confirmAndRun = useCallback(
        (spec, run, options) => {
            requestIdRef.current += 1;
            runRef.current = run;
            cancelRef.current = options?.onCancel || null;
            specRef.current = spec;
            confirmedRef.current = false;
            setJustPurchased(false);
            setPurchaseOpen(false);
            setQuote(null);
            setOpen(true);
            loadQuote(spec);
        },
        [loadQuote]
    );

    const openFromError = useCallback(
        (requestError, run) => {
            requestIdRef.current += 1;
            const data = requestError?.response?.data || {};
            if (run) runRef.current = run;
            confirmedRef.current = true;
            const fallback = {
                coins_required: data.coins_required || 0,
                available: data.available || 0,
                shortfall: data.shortfall || 0,
                sufficient: false,
                can_purchase: data.can_purchase !== false,
                items: data.operation
                    ? [
                          {
                              operation: data.operation,
                              coins_required: data.coins_required,
                          },
                      ]
                    : [],
            };
            setQuote(fallback);
            setOpen(true);
            setLoading(false);
            setError("");
            setJustPurchased(false);
            openPurchaseIfNeeded(fallback);
            const spec = specRef.current || data.operation;
            if (spec) loadQuote(spec, fallback);
        },
        [loadQuote, openPurchaseIfNeeded]
    );

    const handleConfirm = () => {
        confirmedRef.current = true;
        const run = runRef.current;
        setOpen(false);
        setPurchaseOpen(false);
        setJustPurchased(false);
        run?.();
    };

    const handlePurchased = () => {
        setPurchaseOpen(false);
        setJustPurchased(true);
        const reloadSpec =
            specRef.current ||
            (quote?.items?.length
                ? quote.items.map((item) => ({
                      operation: item.operation,
                      quantity: item.quantity || 1,
                  }))
                : null);
        if (!reloadSpec) return;
        loadQuote(reloadSpec).then((data) => {
            if (data?.sufficient && confirmedRef.current) {
                const run = runRef.current;
                setOpen(false);
                setJustPurchased(false);
                run?.();
            }
        });
    };

    const gateContent = (
        <>
            <AiCoinConfirmModal
                show={open}
                quote={quote}
                loading={loading}
                error={error}
                justPurchased={justPurchased}
                onHide={close}
                onConfirm={handleConfirm}
                onBuy={() => setPurchaseOpen(true)}
            />
            <CoinPurchaseModal
                show={purchaseOpen}
                stacked
                note={t("ai.coins.purchase.resume")}
                initialQuantity={Math.max(1, Number(quote?.shortfall) || 1)}
                onHide={() => setPurchaseOpen(false)}
                onPurchased={handlePurchased}
            />
        </>
    );
    const gate =
        typeof document === "undefined"
            ? gateContent
            : createPortal(gateContent, document.body);

    return { confirmAndRun, openFromError, gate, isOpen: open || purchaseOpen };
}

function AiCoinConfirmModal({
    show,
    quote,
    loading,
    error,
    justPurchased,
    onHide,
    onConfirm,
    onBuy,
}) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === "fa" ? "fa-IR" : "en-US";
    const sufficient = Boolean(quote?.sufficient);
    const remaining = Math.max(
        0,
        (Number(quote?.available) || 0) - (Number(quote?.coins_required) || 0)
    );

    return (
        <DBModal
            show={show}
            onHide={onHide}
            stacked
            minWidth={440}
            className="ai-coin-confirm"
            ariaLabelledby="ai-coin-confirm-title"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={
                    <span id="ai-coin-confirm-title">
                        {sufficient
                            ? t("ai.coins.confirm.title")
                            : t("ai.coins.insufficient.title")}
                    </span>
                }
            />
            <DBModal.Content>
                {loading && !quote ? (
                    <p className="ai-coin-confirm__status">{t("ai.coins.loading")}</p>
                ) : error && !quote ? (
                    <p className="ai-coin-confirm__alert" role="alert">
                        {t(error)}
                    </p>
                ) : (
                    <>
                        {justPurchased && sufficient && (
                            <p className="ai-coin-confirm__ready" role="status">
                                {t("ai.coins.purchased")}
                            </p>
                        )}
                        {!sufficient && (
                            <p className="ai-coin-confirm__alert" role="alert">
                                <Warning2 size={18} aria-hidden="true" />
                                <span>
                                    {t("ai.coins.insufficient.body", {
                                        shortfall: formatNumber(
                                            quote?.shortfall || 0,
                                            locale
                                        ),
                                    })}
                                    <span className="ai-coin-confirm__keep">
                                        {t("ai.coins.insufficient.keep.path")}
                                    </span>
                                </span>
                            </p>
                        )}
                        <dl className="ai-coin-confirm__facts">
                            <div>
                                <dt>{t("ai.coins.required")}</dt>
                                <dd>
                                    <CoinGlyph />
                                    <strong>
                                        {formatNumber(
                                            quote?.coins_required || 0,
                                            locale
                                        )}
                                    </strong>
                                    <span>{t("ai.coins.unit")}</span>
                                </dd>
                            </div>
                            <div>
                                <dt>{t("ai.coins.available")}</dt>
                                <dd>
                                    {formatNumber(quote?.available || 0, locale)}
                                    <span>{t("ai.coins.unit")}</span>
                                </dd>
                            </div>
                            {sufficient && (
                                <div>
                                    <dt>{t("ai.coins.remaining")}</dt>
                                    <dd>
                                        {formatNumber(remaining, locale)}
                                        <span>{t("ai.coins.unit")}</span>
                                    </dd>
                                </div>
                            )}
                        </dl>
                        {quote?.items?.length > 0 && (
                            <ul className="ai-coin-confirm__ops">
                                {quote.items.map((item) => (
                                    <li key={`${item.operation}-${item.quantity}`}>
                                        <span>
                                            {t(`ai.coins.op.${item.operation}`, {
                                                defaultValue: item.label,
                                            })}
                                            {item.quantity > 1
                                                ? ` × ${formatNumber(
                                                      item.quantity,
                                                      locale
                                                  )}`
                                                : ""}
                                        </span>
                                        <span>
                                            {formatNumber(
                                                item.coins_required,
                                                locale
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {sufficient && (
                            <p className="ai-coin-confirm__note">
                                <InfoCircle size={16} aria-hidden="true" />
                                {t("ai.coins.confirm.note")}
                            </p>
                        )}
                    </>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    type="secondary"
                    caption={t("cancel")}
                    onClick={onHide}
                />
                {sufficient ? (
                    <DBButton
                        caption={t("ai.coins.confirm.action")}
                        loading={loading}
                        disabled={!quote}
                        onClick={onConfirm}
                    />
                ) : quote?.can_purchase ? (
                    <DBButton
                        caption={t("ai.coins.buy")}
                        onClick={onBuy}
                    />
                ) : (
                    <DBButton
                        caption={t("ai.coins.ask.admin")}
                        disabled
                    />
                )}
            </DBModal.Footer>
        </DBModal>
    );
}
